import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Flex, InputNumber, Slider, Typography } from 'antd';
import { sliderTheme } from '@components/layerStyling/sliderTheme';

const { Text } = Typography;

/**
 * Controlled min/max range input: two InputNumber fields + an optional dual-thumb Slider.
 *
 * Designed to work both standalone and inside a Form.Item (value/onChange contract).
 * The value shape is `{ min: number, max: number }`.
 *
 * Commit-on-release: the slider handle and number fields update live from a local
 * draft, but the value is only lifted to the parent (`onChange`) when the
 * interaction settles — on slider release (`onChangeComplete`) and on input
 * blur / Enter. This keeps the UI responsive while ensuring the (potentially
 * expensive) downstream recompute driven by `onChange` runs once per adjustment
 * instead of on every pointer move or keystroke.
 *
 * InputNumber enforces bounds natively via its own min/max props:
 * - Absolute bounds are enforced on blur (changeOnBlur default).
 * - Cross-field constraint: MIN input's max = current draft max, MAX input's min = current draft min.
 * - Non-numeric input is silently rejected by InputNumber.
 * - Clearing a field emits null; the draft preserves null so the user can retype
 *   without interruption. InputNumber clamps to the absolute bound on blur.
 *
 * @param {{
 *   value?: { min: number, max: number },
 *   onChange?: (value: { min: number, max: number }) => void,
 *   min: number,
 *   max: number,
 *   step?: number,
 *   showSlider?: boolean,
 * }} props
 */
const RangeSliderInput = ({
	value,
	onChange,
	min,
	max,
	precision,
	step = 0.1,
	showSlider = true,
}) => {
	// Committed value from the parent/Form (source of truth). Keep an explicit
	// null (cleared field) distinct from "no value prop yet".
	const committedMin = value ? value.min : min;
	const committedMax = value ? value.max : max;

	// Local draft for live visual feedback during drag/typing. Lifted to the
	// parent only on commit (slider release, input blur/Enter). A ref mirrors the
	// draft synchronously so commit handlers read the latest value within the
	// same event, before the next render.
	const [draft, setDraft] = useState({ min: committedMin, max: committedMax });
	const draftRef = useRef(draft);
	const focusedRef = useRef(false);

	const updateDraft = useCallback(next => {
		draftRef.current = next;
		setDraft(next);
	}, []);

	// Re-sync the draft when the committed value changes externally — e.g. Reset,
	// Apply, or switching the selected object swaps the whole form value. After
	// our own commit this simply re-sets the draft to the value we just lifted.
	// Skipped while focused/dragging — a background committed-value change (e.g.
	// another panel's Apply triggering an effective-bounds re-seed) must not
	// silently discard input the user is actively entering.
	useEffect(() => {
		if (focusedRef.current) return;
		updateDraft({ min: committedMin, max: committedMax });
	}, [committedMin, committedMax, updateDraft]);

	// Draft drives the display. null (cleared field) is preserved for the input;
	// the slider and cross-field constraints fall back to the absolute bounds.
	const rawMin = draft.min;
	const rawMax = draft.max;
	const currentMin = rawMin ?? min;
	const currentMax = rawMax ?? max;

	// ─── Live (local-only) handlers — no lift to parent ──────────────────────

	const handleMinInput = useCallback(
		// draftRef.current avoids stale closure when both fields update in rapid succession
		val => updateDraft({ ...draftRef.current, min: val }),
		[updateDraft]
	);

	const handleMaxInput = useCallback(
		// draftRef.current avoids stale closure when both fields update in rapid succession
		val => updateDraft({ ...draftRef.current, max: val }),
		[updateDraft]
	);

	const handleSliderInput = useCallback(
		([newMin, newMax]) => updateDraft({ min: newMin, max: newMax }),
		[updateDraft]
	);

	const handleSliderFocus = useCallback(() => {
		focusedRef.current = true;
	}, []);

	const handleSliderBlur = useCallback(() => {
		focusedRef.current = false;
	}, []);

	// ─── Commit handlers — lift the settled draft to the parent ──────────────

	const commit = useCallback(() => {
		focusedRef.current = false;
		const clamp = (v, lo, hi) => (v == null ? v : Math.min(Math.max(v, lo), hi));
		const nextMin = clamp(draftRef.current.min, min, max);
		const nextMax = clamp(draftRef.current.max, min, max);
		const next = {
			min: nextMin != null && nextMax != null ? Math.min(nextMin, nextMax) : nextMin,
			max: nextMin != null && nextMax != null ? Math.max(nextMin, nextMax) : nextMax,
		};
		updateDraft(next);
		onChange?.(next);
	}, [onChange, min, max, updateDraft]);

	const handleFocus = useCallback(() => {
		focusedRef.current = true;
	}, []);

	const marks = useMemo(
		() => ({
			[min]: {
				label: String(min),
				style: { fontSize: '0.7rem', whiteSpace: 'nowrap', transform: 'translateX(0%)' },
			},
			[max]: {
				label: String(max),
				style: { fontSize: '0.7rem', whiteSpace: 'nowrap', transform: 'translateX(-100%)' },
			},
		}),
		[min, max]
	);

	return (
		<Flex vertical gap={8} style={{ width: '100%' }}>
			{/* Min / Max inputs */}
			<Flex gap={16}>
				<Flex vertical style={{ flex: 1 }}>
					<Text type="secondary" style={{ fontSize: '0.75rem', marginBottom: 4 }}>
						MIN
					</Text>
					<InputNumber
						value={rawMin}
						min={min}
						max={currentMax}
						step={step}
						precision={precision}
						onChange={handleMinInput}
						onFocus={handleFocus}
						onBlur={commit}
						onPressEnter={commit}
						style={{ width: '100%' }}
						controls={false}
						aria-label="Minimum value"
					/>
				</Flex>
				<Flex vertical style={{ flex: 1 }}>
					<Text type="secondary" style={{ fontSize: '0.75rem', marginBottom: 4 }}>
						MAX
					</Text>
					<InputNumber
						value={rawMax}
						min={currentMin}
						max={max}
						step={step}
						precision={precision}
						onChange={handleMaxInput}
						onFocus={handleFocus}
						onBlur={commit}
						onPressEnter={commit}
						style={{ width: '100%' }}
						controls={false}
						aria-label="Maximum value"
					/>
				</Flex>
			</Flex>

			{/* Slider + bound labels via marks at track edges.
			    onChange updates the local draft live; onChangeComplete lifts on release. */}
			{showSlider && (
				<ConfigProvider theme={sliderTheme}>
					<Slider
						range
						min={min}
						max={max}
						step={step}
						value={[currentMin, currentMax]}
						onChange={handleSliderInput}
						onFocus={handleSliderFocus}
						onBlur={handleSliderBlur}
						onChangeComplete={commit}
						tooltip={{ open: false }}
						marks={marks}
						style={{ margin: '4px 0 0' }}
					/>
				</ConfigProvider>
			)}
		</Flex>
	);
};

export default RangeSliderInput;
